import { CodeFile } from 'mini-code'
import { useEffect, useRef } from 'react'
import { useTabsStore } from './store.ts'

interface TabUpdateManagerProps {
  codeFileA: CodeFile
  codeFileB: CodeFile
  activeTabId: string
  channelMode: 'single' | 'dual'
}

export function TabUpdateManager({
  codeFileA,
  codeFileB,
  activeTabId,
  channelMode,
}: TabUpdateManagerProps) {
  // Use getState to avoid subscribing and causing re-renders
  const getTabs = useTabsStore.getState
  const prevSourceCodeA = useRef(codeFileA.value)
  const prevSourceCodeB = useRef(codeFileB.value)
  const prevActiveTabIdRef = useRef<string | null>(null)
  const updateTimeoutRefA = useRef<NodeJS.Timeout | null>(null)
  const updateTimeoutRefB = useRef<NodeJS.Timeout | null>(null)
  const scrollCheckIntervalRef = useRef<number | null>(null)
  const prevScrollStateA = useRef<{ scrollX: number; scrollY: number } | null>(null)
  const prevScrollStateB = useRef<{ scrollX: number; scrollY: number } | null>(null)

  useEffect(() => {
    if (channelMode === 'dual') return

    // When activeTabId changes, update refs from the tab's stored code
    // This prevents false dirty marks when switching tabs
    if (prevActiveTabIdRef.current !== activeTabId) {
      const tabs = getTabs().tabs
      const tab = tabs.find(t => t.id === activeTabId)
      if (tab) {
        prevSourceCodeA.current = tab.codeA
        prevSourceCodeB.current = tab.codeB
        // Initialize scroll state from saved state
        if (tab.codeFileStateA) {
          prevScrollStateA.current = {
            scrollX: tab.codeFileStateA.scrollX ?? 0,
            scrollY: tab.codeFileStateA.scrollY ?? 0,
          }
        }
        else {
          prevScrollStateA.current = null
        }
        if (tab.codeFileStateB) {
          prevScrollStateB.current = {
            scrollX: tab.codeFileStateB.scrollX ?? 0,
            scrollY: tab.codeFileStateB.scrollY ?? 0,
          }
        }
        else {
          prevScrollStateB.current = null
        }
      }
      else {
        // Fallback to current CodeFile values if tab not found
        prevSourceCodeA.current = codeFileA.value
        prevSourceCodeB.current = codeFileB.value
        prevScrollStateA.current = null
        prevScrollStateB.current = null
      }
      prevActiveTabIdRef.current = activeTabId
    }

    const unsubscribeA = codeFileA.subscribe(() => {
      const newCode = codeFileA.value
      // Only update if code actually changed (not just selection/caret)
      if (prevSourceCodeA.current !== newCode) {
        prevSourceCodeA.current = newCode

        // Debounce the tab update
        if (updateTimeoutRefA.current) {
          clearTimeout(updateTimeoutRefA.current)
        }
        updateTimeoutRefA.current = setTimeout(() => {
          updateTimeoutRefA.current = null
          // Always read the latest code values when the timeout fires
          const latestCodeA = codeFileA.value
          const latestCodeB = codeFileB.value
          // Use getState to update without subscribing - this prevents App re-renders
          const { updateTab, tabs } = getTabs()
          const currentTab = tabs.find(t => t.id === activeTabId)
          // Only update if code actually changed in the store
          // This prevents unnecessary store updates when slider moves but code in store is same
          if (currentTab && (currentTab.codeA !== latestCodeA || currentTab.codeB !== latestCodeB)) {
            // Only include codeFileState if code actually changed to prevent unnecessary state updates
            const updates: any = {
              codeA: latestCodeA,
              codeB: latestCodeB,
              isDirty: true,
            }
            // Only include codeFileState if code changed (not just selection/caret)
            if (currentTab.codeA !== latestCodeA) {
              updates.codeFileStateA = codeFileA.getState()
            }
            if (currentTab.codeB !== latestCodeB) {
              updates.codeFileStateB = codeFileB.getState()
            }
            updateTab(activeTabId, updates)
          }
        }, 300)
      }
    })

    const unsubscribeB = codeFileB.subscribe(() => {
      const newCode = codeFileB.value
      // Only update if code actually changed (not just selection/caret)
      if (prevSourceCodeB.current !== newCode) {
        prevSourceCodeB.current = newCode

        // Debounce the tab update
        if (updateTimeoutRefB.current) {
          clearTimeout(updateTimeoutRefB.current)
        }
        updateTimeoutRefB.current = setTimeout(() => {
          updateTimeoutRefB.current = null
          // Always read the latest code values when the timeout fires
          const latestCodeA = codeFileA.value
          const latestCodeB = codeFileB.value
          // Use getState to update without subscribing - this prevents App re-renders
          const { updateTab, tabs } = getTabs()
          const currentTab = tabs.find(t => t.id === activeTabId)
          // Only update if code actually changed in the store
          // This prevents unnecessary store updates when slider moves but code in store is same
          if (currentTab && (currentTab.codeA !== latestCodeA || currentTab.codeB !== latestCodeB)) {
            // Only include codeFileState if code actually changed to prevent unnecessary state updates
            const updates: any = {
              codeA: latestCodeA,
              codeB: latestCodeB,
              isDirty: true,
            }
            // Only include codeFileState if code changed (not just selection/caret)
            if (currentTab.codeA !== latestCodeA) {
              updates.codeFileStateA = codeFileA.getState()
            }
            if (currentTab.codeB !== latestCodeB) {
              updates.codeFileStateB = codeFileB.getState()
            }
            updateTab(activeTabId, updates)
          }
        }, 300)
      }
    })

    // Initialize scroll state on first mount if not set
    if (prevScrollStateA.current === null) {
      const stateA = codeFileA.getState()
      prevScrollStateA.current = {
        scrollX: stateA.scrollX ?? 0,
        scrollY: stateA.scrollY ?? 0,
      }
    }
    if (prevScrollStateB.current === null) {
      const stateB = codeFileB.getState()
      prevScrollStateB.current = {
        scrollX: stateB.scrollX ?? 0,
        scrollY: stateB.scrollY ?? 0,
      }
    }

    // Poll for scroll changes and save state
    const checkScrollChanges = () => {
      const stateA = codeFileA.getState()
      const stateB = codeFileB.getState()
      const scrollA = { scrollX: stateA.scrollX ?? 0, scrollY: stateA.scrollY ?? 0 }
      const scrollB = { scrollX: stateB.scrollX ?? 0, scrollY: stateB.scrollY ?? 0 }

      const { updateTab, tabs } = getTabs()
      const currentTab = tabs.find(t => t.id === activeTabId)
      if (!currentTab) return

      const updates: any = {}
      let hasScrollChanges = false

      if (prevScrollStateA.current) {
        if (
          prevScrollStateA.current.scrollX !== scrollA.scrollX
          || prevScrollStateA.current.scrollY !== scrollA.scrollY
        ) {
          updates.codeFileStateA = stateA
          hasScrollChanges = true
        }
      }
      else {
        updates.codeFileStateA = stateA
        hasScrollChanges = true
      }

      if (prevScrollStateB.current) {
        if (
          prevScrollStateB.current.scrollX !== scrollB.scrollX
          || prevScrollStateB.current.scrollY !== scrollB.scrollY
        ) {
          updates.codeFileStateB = stateB
          hasScrollChanges = true
        }
      }
      else {
        updates.codeFileStateB = stateB
        hasScrollChanges = true
      }

      if (hasScrollChanges) {
        prevScrollStateA.current = scrollA
        prevScrollStateB.current = scrollB
        updateTab(activeTabId, updates)
      }
    }

    scrollCheckIntervalRef.current = setInterval(checkScrollChanges, 500) as unknown as number

    // Watch for tab code updates when isDirty becomes false (after save)
    // This syncs our refs to prevent false dirty marks
    const unsubscribeTabs = useTabsStore.subscribe(
      state => {
        const currentTab = state.tabs.find(t => t.id === activeTabId)
        return currentTab ? { isDirty: currentTab.isDirty, codeA: currentTab.codeA, codeB: currentTab.codeB } : null
      },
      (tabState) => {
        if (tabState && !tabState.isDirty) {
          // If tab is not dirty and code matches current code, sync refs
          if (tabState.codeA === codeFileA.value && tabState.codeB === codeFileB.value) {
            prevSourceCodeA.current = codeFileA.value
            prevSourceCodeB.current = codeFileB.value
          }
        }
      },
      (a, b) => a?.isDirty === b?.isDirty && a?.codeA === b?.codeA && a?.codeB === b?.codeB
    )

    return () => {
      unsubscribeA()
      unsubscribeB()
      if (updateTimeoutRefA.current) {
        clearTimeout(updateTimeoutRefA.current)
        updateTimeoutRefA.current = null
      }
      if (updateTimeoutRefB.current) {
        clearTimeout(updateTimeoutRefB.current)
        updateTimeoutRefB.current = null
      }
      if (scrollCheckIntervalRef.current) {
        clearInterval(scrollCheckIntervalRef.current)
        scrollCheckIntervalRef.current = null
      }
      unsubscribeTabs()
      prevScrollStateA.current = null
      prevScrollStateB.current = null
    }
  }, [activeTabId, codeFileA, codeFileB, channelMode])

  return null
}
